import { focusWithin } from "#ce/utils/focus";

/**
 * Handles clicks on the activator and on the menu content.
 *
 * @param {object} options
 * @param {import("vue").Ref<boolean>} options.isActive - the menu's open state.
 * @param {import("vue").Ref<boolean>} options.disabled - whether the menu is disabled.
 * @param {import("vue").Ref<boolean>} options.closeOnContentClick - whether a click on the content closes the menu.
 * @param {import("vue").Ref<boolean>} options.nativeContentClicks - preserves default actions and propagation when enabled.
 * @param {import("vue").Ref<boolean>} options.showBottomSheet - whether the menu renders as a bottom sheet.
 * @param {import("vue").Ref<HTMLElement | null>} options.activator - the element wrapping the activator slot.
 * @param {() => HTMLElement | null | undefined} options.activatorControl - returns the slotted activator control.
 * @returns {{ onActivatorClick: () => void, onContentClick: (event: Event) => void }}
 */
export function useMenuClicks({
  isActive,
  disabled,
  closeOnContentClick,
  nativeContentClicks,
  showBottomSheet,
  activator,
  activatorControl,
}) {
  /** Toggles the menu unless it is disabled, and focuses the activator control when the popup opens. */
  function onActivatorClick() {
    // sp-menu is not form-associated, so it must enforce its own disabled state —
    // there is no native :disabled suppression to fall back on.
    if (disabled.value) {
      return;
    }

    isActive.value = !isActive.value;

    // Safari leaves a clicked button unfocused, and Escape only reaches a menu that holds focus.
    if (isActive.value && !showBottomSheet.value && !focusWithin(activator.value)) {
      activatorControl()?.focus({ preventScroll: true });
    }
  }

  /**
   * Prevents the click's default action and stops its propagation unless `nativeContentClicks` is on, then
   * closes the menu when `closeOnContentClick` is on.
   */
  function onContentClick(event) {
    if (!nativeContentClicks.value) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (closeOnContentClick.value && isActive.value) {
      isActive.value = false;
    }
  }

  return { onActivatorClick, onContentClick };
}
